/**
 * Zeitraum-Chips des Monats-Editors (Board „Monat je Apartment“).
 *
 * Jeder Chip öffnet den freien Kalender und lässt sich darin beliebig — auch über mehrere Monate
 * und außerhalb des sichtbaren Fensters — neu setzen; „+ Weiteren Zeitraum“ nutzt denselben
 * Kalender. Das Raster rahmt davon nur den sichtbaren Teil.
 */
import { CalendarDays, Plus, X } from 'lucide-react';
import { rangeChipLabel } from './calendarFormat';
import { RangePickerPopover } from './RangePickerPopover';
import type { IsoRange } from './rangePickerModel';
import { MAX_RANGES, type SelectedRange } from './rangeSelectionModel';

interface RangeChipListProps {
  ranges: readonly SelectedRange[];
  today: string;
  disabled: boolean;
  onRemove: (id: string) => void;
  onEdit: (id: string, range: IsoRange) => void;
  onAdd: (range: IsoRange) => void;
}

export function RangeChipList({
  ranges,
  today,
  disabled,
  onRemove,
  onEdit,
  onAdd,
}: RangeChipListProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {ranges.map((range) => {
        const label = rangeChipLabel(range.from, range.to);
        return (
          <span
            key={range.id}
            className="bg-forest-soft text-forest-deep rounded-pill inline-flex min-h-11 items-center gap-0.5 pl-1 pr-1 text-[13px] font-bold"
          >
            <RangePickerPopover
              today={today}
              initial={{ from: range.from, to: range.to }}
              title="Zeitraum ändern"
              applyLabel="Übernehmen"
              onApply={(next) => onEdit(range.id, next)}
            >
              <button
                type="button"
                disabled={disabled}
                aria-label={`Zeitraum ${label} im Kalender ändern`}
                className="focus-visible:ring-forest rounded-pill inline-flex min-h-11 items-center gap-1.5 pl-2.5 pr-1 hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2"
              >
                <CalendarDays size={14} aria-hidden />
                {label}
              </button>
            </RangePickerPopover>
            <button
              type="button"
              onClick={() => onRemove(range.id)}
              disabled={disabled}
              aria-label={`Zeitraum ${label} entfernen`}
              className="focus-visible:ring-forest grid size-11 place-items-center rounded-full hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2"
            >
              <X size={12} aria-hidden />
            </button>
          </span>
        );
      })}
      {ranges.length < MAX_RANGES && (
        <RangePickerPopover
          today={today}
          title={ranges.length === 0 ? 'Zeitraum wählen' : 'Weiteren Zeitraum wählen'}
          applyLabel="Hinzufügen"
          onApply={onAdd}
        >
          <button
            type="button"
            disabled={disabled}
            className="rounded-pill border-line text-ink hover:border-forest inline-flex min-h-11 items-center gap-1.5 border px-3 text-[13px] font-bold disabled:opacity-40"
          >
            <Plus size={13} aria-hidden />
            {ranges.length === 0 ? 'Zeitraum frei wählen' : 'Weiteren Zeitraum'}
          </button>
        </RangePickerPopover>
      )}
    </div>
  );
}
